import {
  useValidateCarrier,
  NO_PHYSICAL_RETURN_CODE,
} from "@babackapp/return-widget-components/mutations/use-validate-carrier";
import { useDraftReturn } from "@babackapp/return-widget-components/hooks/use-draft-return";
import { useFinancialSummary } from "@babackapp/return-widget-components/hooks/use-financial-summary";
import { hasIncompleteLineItems } from "@babackapp/return-widget-components/lib/draft.lib";
import { usePublicOrder } from "@babackapp/return-widget-components/queries/use-public-order";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useWidgetParams } from "./use-widget-params";

/**
 * Handles the "Confirm" action on the summary page:
 * 1. Calls POST /public/return-request/validate-carrier with the current draft
 * 2. If the backend says "no-physical-return" → navigate to explanation page (skipping return-mode)
 * 3. Otherwise → navigate to return-mode as usual
 */
export function useSummaryConfirm() {
  const navigate = useNavigate();
  const { orderId } = useWidgetParams();
  const draft = useDraftReturn(orderId);
  const { data: order } = usePublicOrder(orderId);
  const summary = useFinancialSummary(orderId, order ?? null);
  const validateCarrier = useValidateCarrier();
  const [isValidating, setIsValidating] = useState(false);

  const onConfirm = useCallback(async () => {
    // BBS-249: never proceed past summary with an item missing
    // reasonId/resolution (e.g. an interrupted resolution step) — see
    // incident on return d0Z6vB10FUxtyEk5J1 where this reached the backend
    // as resolution: null. Checked again at the final confirm step in
    // use-complete.ts.
    if (!draft || hasIncompleteLineItems(draft)) return;
    // Exchange-credit goes to shop-now without validate-carrier
    if (summary.exchangeCreditTotal > 0) {
      navigate("../shop-now");
      return;
    }
    setIsValidating(true);
    try {
      // The address step comes later: until then the order's address picks
      // the carrier, and the backend rejects a null one.
      const result = await validateCarrier.mutateAsync({
        ...draft,
        address: draft.address ?? order?.shippingAddress ?? null,
      });
      navigate(
        result?.code === NO_PHYSICAL_RETURN_CODE
          ? "../no-physical-return"
          : "../return-mode",
      );
    } catch {
      navigate("../return-mode");
    } finally {
      setIsValidating(false);
    }
  }, [
    draft,
    order?.shippingAddress,
    navigate,
    validateCarrier,
    summary.exchangeCreditTotal,
  ]);

  return { onConfirm, isValidating };
}
